import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { ForgedOpsAttribution } from "@/components/ForgedOpsAttribution";

/**
 * /legal/terms — Terms of Service.
 *
 * ForgedOps LLC owns and operates the FORGEDOPS platform. This page
 * sets out the terms under which customers and their authorized users
 * may access and use the platform. Treat the wording inside <article>
 * as legal text — do not edit phrasing without explicit owner approval.
 */
export default function TermsOfService() {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <main className="min-h-screen bg-slate-50">
      <header className="border-b-2 border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/" className="text-slate-600 hover:text-slate-900" data-testid="terms-back-link">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <FileText className="w-6 h-6 text-slate-700" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600 font-bold">
              Legal
            </div>
            <h1 className="font-display text-xl font-black text-slate-900">
              Terms of Service
            </h1>
          </div>
        </div>
      </header>

      <article
        className="max-w-3xl mx-auto px-4 sm:px-6 py-8 prose prose-slate prose-sm sm:prose-base"
        data-testid="terms-of-service-page"
      >
        <p className="text-xs font-mono uppercase tracking-wide text-slate-500 mb-6">
          Effective: {today} · ForgedOps LLC · forgedopshq.com
        </p>

        <p>
          These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;)
          govern access to and use of the <strong>FORGEDOPS</strong>{" "}
          field-operations and safety-documentation platform (the
          &ldquo;<strong>Platform</strong>&rdquo;), owned and operated by{" "}
          <strong>ForgedOps LLC</strong> (&ldquo;<strong>ForgedOps</strong>,&rdquo;
          &ldquo;<strong>we</strong>,&rdquo; or &ldquo;<strong>us</strong>&rdquo;).
        </p>

        <hr className="my-6 border-slate-200" />

        <h2>1. Acceptance</h2>
        <p>
          By accessing the Platform, the customer organization
          (&ldquo;<strong>Customer</strong>&rdquo;) and its authorized
          users (&ldquo;<strong>Users</strong>&rdquo;) agree to these Terms.
          If you do not agree, do not use the Platform.
        </p>

        <h2>2. License Grant</h2>
        <p>
          Subject to these Terms, ForgedOps grants the Customer a
          non-exclusive, non-transferable, revocable license to access
          and use the Platform for the Customer&rsquo;s internal
          operations during the term of the applicable agreement.
        </p>

        <h2>3. Ownership &amp; Intellectual Property</h2>
        <p>
          ForgedOps LLC owns and retains all right, title, and interest
          in and to the Platform, including the source code, software,
          UI/UX, designs, workflows, methodologies, and trademarks. No
          rights are granted to the Customer except as expressly set out
          in these Terms.
        </p>

        <h2>4. Customer Data</h2>
        <p>
          The Customer retains ownership of all data submitted through
          the Platform (&ldquo;<strong>Customer Data</strong>&rdquo;).
          ForgedOps LLC stores and processes Customer Data solely to
          provide and maintain the Platform on the Customer&rsquo;s
          behalf. ForgedOps does not sell, rent, or share Customer Data
          with third parties for marketing.
        </p>

        <h2>5. Acceptable Use</h2>
        <ul>
          <li>Do not use the Platform for unlawful purposes.</li>
          <li>Do not attempt to disrupt, reverse-engineer, or copy the Platform.</li>
          <li>Do not upload malicious code or attempt to gain unauthorized access.</li>
          <li>Do not share credentials or expose tokens.</li>
        </ul>

        <h2>6. White-Label Deployments</h2>
        <p>
          The Platform supports per-customer branding (logo, palette,
          terminology, generated PDFs). Each white-label deployment is
          configured for the Customer&rsquo;s operations and remains the
          intellectual property of ForgedOps LLC.
        </p>

        <h2>7. Availability</h2>
        <p>
          ForgedOps LLC strives for high availability but does not
          guarantee uninterrupted access, uptime, or backup recoverability
          beyond what is set out in the applicable agreement. Scheduled
          maintenance and security patches may interrupt access.
        </p>

        <h2>8. Suspension &amp; Termination</h2>
        <p>
          ForgedOps LLC may suspend or terminate access for breach of
          these Terms, security events, non-payment, or end of the
          applicable agreement.
        </p>

        <h2>9. Disclaimer</h2>
        <p>
          The Platform is provided &ldquo;<strong>as is</strong>&rdquo;
          and &ldquo;<strong>as available</strong>.&rdquo; To the maximum
          extent permitted by law, ForgedOps LLC disclaims all warranties,
          express or implied, including merchantability, fitness for a
          particular purpose, and non-infringement.
        </p>

        <h2>10. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, ForgedOps LLC is not
          liable for indirect, incidental, special, consequential, or
          punitive damages, including lost profits or revenue.
        </p>

        <h2>11. Demonstration Environment</h2>
        <p>
          When the Platform is presented as a demonstration environment
          (e.g. <em>forgeops.demo</em>), the data shown is sample data
          for demonstration purposes only and does not represent any
          real Customer&rsquo;s operations.
        </p>

        <h2>12. Changes</h2>
        <p>
          ForgedOps LLC may update these Terms at any time. Material
          changes will be posted on this page with a new effective date.
        </p>

        <h2>13. Contact</h2>
        <p>
          Questions about these Terms should be directed to{" "}
          <a href="https://forgedopshq.com" target="_blank" rel="noopener noreferrer">
            forgedopshq.com
          </a>
          .
        </p>

        <p className="text-xs font-mono uppercase tracking-wide text-slate-500 mt-8">
          ForgedOps LLC · Operational infrastructure for heavy-civil contractors
        </p>

        <div className="mt-10 pt-6 border-t border-slate-200">
          <ForgedOpsAttribution variant="global" />
        </div>
      </article>
    </main>
  );
}
